import React, { useEffect, useState } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Flex, Text, Box, HStack, Select, Input, Tag,
  Alert, AlertIcon, useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import { ImmDecision, listarDecisiones } from '../../../api/immApi';
import { fmtDateTime, IMM_COLOR, IMM_COLOR_HOVER, TIPO_DECISION_LABEL } from '../_shared';
import FormularioDecision from './FormularioDecision';

const expedienteLabel = (item: ImmDecision) => item.expediente?.folio ?? `#${item.expedienteId}`;
const decisionLabel = (item: ImmDecision) => TIPO_DECISION_LABEL[item.tipoDecision] ?? item.tipoDecision;

const TablaDecisiones: React.FC = () => {
  const toast = useToast();

  const verD = useDisclosure();
  const nuevoD = useDisclosure();

  const [data, setData] = useState<ImmDecision[]>([]);
  const [selected, setSelected] = useState<ImmDecision | null>(null);
  const [cargando, setCargando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try {
      setData(await listarDecisiones());
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const datosFiltrados = data.filter((item) => {
    const matchTipo = filtroTipo ? item.tipoDecision === filtroTipo : true;
    const q = busqueda.toLowerCase();
    const matchBusqueda = q
      ? expedienteLabel(item).toLowerCase().includes(q) ||
        decisionLabel(item).toLowerCase().includes(q)
      : true;
    return matchTipo && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const handleGuardado = () => { nuevoD.onClose(); fetchData(); toast({ title: 'Registro inmutable agregado', status: 'info', duration: 2500 }); };

  const handleExportExcel = () => {
    const filas = datosFiltrados.map((item) => ({
      'Expediente': expedienteLabel(item),
      'Fecha': fmtDateTime(item.fecha),
      'Decisión': decisionLabel(item),
      'Observaciones': item.observaciones || '',
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Decisiones IMM');
    XLSX.writeFile(wb, `Decisiones_IMM_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box>
      <Alert status="info" borderRadius="md" mb={4} fontSize="sm">
        <AlertIcon />
        Las decisiones se registran de forma cronológica y no pueden modificarse ni eliminarse (registro inmutable).
      </Alert>

      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input placeholder="Buscar por expediente o decisión..." value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }} size="sm" w={{ base: 'full', md: '280px' }} />
          <Select placeholder="Tipo de decisión" value={filtroTipo} onChange={(e) => { setFiltroTipo(e.target.value); setCurrentPage(1); }} size="sm" w="200px">
            {Object.entries(TIPO_DECISION_LABEL).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={nuevoD.onOpen}>Nueva decisión</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg={IMM_COLOR}>
            <Tr>
              <Th color="white" fontSize="xs">Expediente</Th>
              <Th color="white" fontSize="xs">Fecha</Th>
              <Th color="white" fontSize="xs">Decisión</Th>
              <Th color="white" fontSize="xs">Observaciones</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={5} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={5} textAlign="center" py={8}><Text color="gray.400">No se encontraron decisiones</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td><Tag size="sm" colorScheme="red" variant="subtle">{expedienteLabel(item)}</Tag></Td>
                  <Td>{fmtDateTime(item.fecha)}</Td>
                  <Td><Text noOfLines={1} fontSize="sm" fontWeight="medium">{decisionLabel(item)}</Text></Td>
                  <Td><Text noOfLines={1} fontSize="sm" color="gray.600">{item.observaciones || '—'}</Text></Td>
                  <Td>
                    <Button size="xs" colorScheme="blue" variant="outline" onClick={() => { setSelected(item); verD.onOpen(); }}>Ver</Button>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}</Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? IMM_COLOR : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>{page}</Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Ver */}
      <Modal isOpen={verD.isOpen} onClose={verD.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Decisión</ModalHeader>
          <ModalBody py={5}>{selected && <FormularioDecision modo="view" data={selected} />}</ModalBody>
          <ModalFooter><Button onClick={verD.onClose}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      {/* Nueva */}
      <Modal isOpen={nuevoD.isOpen} onClose={nuevoD.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Nueva decisión</ModalHeader>
          <ModalBody py={5}><FormularioDecision modo="new" onGuardado={handleGuardado} onCancelar={nuevoD.onClose} /></ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TablaDecisiones;
